/**
 * AI 服务
 * 提供通用的 AI 调用功能，供所有应用调用
 *
 * ⚠️ 警告：此服务是前端直接调用 AI API（如阿里云 Qwen API）
 *
 * 安全问题：
 * 1. API Key 会暴露在前端代码中，存在安全风险
 * 2. 浏览器直接调用第三方 API 会遇到 CORS 跨域限制
 * 3. 无法统一管理和监控 AI 调用
 *
 * 推荐做法：
 * 1. 使用后端 API 代理：前端调用后端接口（如 /ai/analyze），后端再调用 AI 服务
 * 2. 使用 useCompsAi Hook：默认通过后端 API 调用，更安全可靠
 *
 * 默认配置：
 * - API Key: sk-79bb1c54a76d4fbba359a4f49060873a（来自 qwenConfig.example.ts）
 * - 模型: qwen-turbo
 * - 端点: https://dashscope.aliyuncs.com/api/v1/services/aigc/text-generation/generation
 *
 * @deprecated 建议使用后端 API 调用方式，而不是直接在前端调用 AI 服务
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/**
 * AI 服务类
 */
var AiService = /** @class */ (function () {
    function AiService() {
    }
    /**
     * 在不同构建工具下读取环境变量
     * - CRA: process.env.REACT_APP_*
     * - Next: process.env.NEXT_PUBLIC_*
     * - Vite: import.meta.env.VITE_*
     *
     * 注意：在浏览器生产环境里 `process` 可能不存在，需要做兼容。
     */
    AiService.getEnv = function (key) {
        var _a;
        // CRA / Next（Node 或部分 bundler 注入）
        var fromProcess = typeof process !== "undefined" &&
            (process === null || process === void 0 ? void 0 : process.env) &&
            process.env[key];
        if (typeof fromProcess === "string" && fromProcess.length > 0) {
            return fromProcess;
        }
        // Vite（浏览器运行时）
        // ⚠️ 注意：CRA/Webpack 会把 `import.meta` 视为语法错误，因此必须避免直接引用。
        try {
            // eslint-disable-next-line no-new-func
            var meta = new Function("return import.meta")();
            var fromImportMeta = (_a = meta === null || meta === void 0 ? void 0 : meta.env) === null || _a === void 0 ? void 0 : _a[key];
            if (typeof fromImportMeta === "string" && fromImportMeta.length > 0) {
                return fromImportMeta;
            }
        }
        catch (_b) {
            // 非 Vite 环境：忽略
        }
        return undefined;
    };
    /**
     * 获取默认配置（从环境变量或默认值读取）
     * 注意：在生产环境中，建议通过后端 API 调用，而不是直接在前端配置 API Key
     */
    AiService.getDefaultConfig = function () {
        return {
            endpoint: AiService.getEnv("REACT_APP_AI_ENDPOINT") ||
                AiService.getEnv("NEXT_PUBLIC_AI_ENDPOINT") ||
                AiService.getEnv("VITE_AI_ENDPOINT") ||
                "https://dashscope.aliyuncs.com/api/v1/services/aigc/text-generation/generation",
            apiKey: this.DEFAULT_API_KEY,
            model: AiService.getEnv("REACT_APP_AI_MODEL") ||
                AiService.getEnv("NEXT_PUBLIC_AI_MODEL") ||
                AiService.getEnv("VITE_AI_MODEL") ||
                "qwen-turbo",
            useQwen: true,
            qwenParams: {
                temperature: 0.7,
                max_tokens: 2000,
            },
        };
    };
    /**
     * 设置 AI 服务配置
     * 如果不传入配置，将使用默认配置（从环境变量读取）
     */
    AiService.setConfig = function (config) {
        if (config) {
            this.config = config;
        }
        else {
            // 使用默认配置（从环境变量读取）
            this.config = this.getDefaultConfig();
        }
    };
    /**
     * 获取当前配置
     */
    AiService.getConfig = function () {
        return this.config;
    };
    /**
     * 初始化服务（使用默认配置）
     * 从环境变量读取配置并初始化服务
     */
    AiService.init = function () {
        this.setConfig();
    };
    /**
     * 构建 Qwen API 请求体
     */
    AiService.buildQwenRequestBody = function (params) {
        var _a, _b, _c, _d;
        var data = params.data, thinkingMode = params.thinkingMode, selectedFields = params.selectedFields;
        var config = this.config;
        // 构建提示词
        var prompt = thinkingMode.prompt ||
            "\u8BF7\u4EE5".concat(thinkingMode.label, "\u7684\u65B9\u5F0F\u5904\u7406\u4EE5\u4E0B\u6570\u636E\uFF1A");
        // 格式化数据
        var dataText = typeof data === "string"
            ? data
            : JSON.stringify(data, null, 2);
        // 构建完整的用户消息
        var userMessage = prompt;
        if (selectedFields && selectedFields.length > 0) {
            userMessage += "\n\n\u9009\u4E2D\u7684\u5B57\u6BB5\uFF1A".concat(selectedFields.join(", "));
        }
        userMessage += "\n\n\u6570\u636E\u5185\u5BB9\uFF1A\n".concat(dataText);
        // Qwen API 请求体格式
        return {
            model: config.model || "qwen-turbo",
            input: {
                messages: [
                    {
                        role: "user",
                        content: userMessage,
                    },
                ],
            },
            parameters: __assign({ temperature: (_b = (_a = config.qwenParams) === null || _a === void 0 ? void 0 : _a.temperature) !== null && _b !== void 0 ? _b : 0.7, max_tokens: (_c = config.qwenParams) === null || _c === void 0 ? void 0 : _c.max_tokens, top_p: (_d = config.qwenParams) === null || _d === void 0 ? void 0 : _d.top_p }, config.qwenParams),
        };
    };
    /**
     * 解析 Qwen API 响应
     */
    AiService.parseQwenResponse = function (response) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        console.log("🔍 [AiService] 解析响应:", {
            hasOutput: !!response.output,
            hasChoices: !!((_a = response.output) === null || _a === void 0 ? void 0 : _a.choices),
            hasText: !!((_b = response.output) === null || _b === void 0 ? void 0 : _b.text),
            responseKeys: Object.keys(response),
        });
        // Qwen API 响应格式（标准格式）
        if ((_f = (_e = (_d = (_c = response.output) === null || _c === void 0 ? void 0 : _c.choices) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.message) === null || _f === void 0 ? void 0 : _f.content) {
            return response.output.choices[0].message.content;
        }
        // Qwen API 响应格式（简化格式）
        if ((_g = response.output) === null || _g === void 0 ? void 0 : _g.text) {
            return response.output.text;
        }
        // 兼容其他可能的响应格式
        if ((_j = (_h = response.output) === null || _h === void 0 ? void 0 : _h.message) === null || _j === void 0 ? void 0 : _j.content) {
            return response.output.message.content;
        }
        if (response.content) {
            return response.content;
        }
        if (response.message) {
            return response.message;
        }
        if (response.text) {
            return response.text;
        }
        // 如果无法解析，返回完整的响应以便调试
        console.error("❌ [AiService] 无法解析响应，原始响应:", response);
        throw new Error("\u65E0\u6CD5\u89E3\u6790 Qwen API \u54CD\u5E94\u3002\u54CD\u5E94\u683C\u5F0F: ".concat(JSON.stringify(response).substring(0, 200)));
    };
    /**
     * 调用 AI 接口
     * @param params AI 调用参数
     * @returns AI 响应结果
     */
    AiService.callAi = function (params) {
        var _a, _b, _c, _d, _e, _f, _g;
        return __awaiter(this, void 0, Promise, function () {
            var config, useQwen, endpoint, headers, qwenEndpoint, requestBody, response, fetchError_1, errorData, errorMessage, result, requestBody, response, result, error_1, errorMessage, finalMessage;
            return __generator(this, function (_h) {
                switch (_h.label) {
                    case 0:
                        // 如果未配置，尝试使用默认配置（从环境变量读取）
                        if (!this.config) {
                            console.warn("⚠️ [AiService] 未配置，尝试使用默认配置（从环境变量读取）");
                            this.setConfig();
                        }
                        if (!this.config) {
                            throw new Error("AI 服务未配置，请先调用 AiService.setConfig() 设置配置，或设置环境变量 REACT_APP_AI_API_KEY");
                        }
                        _h.label = 1;
                    case 1:
                        _h.trys.push([1, 13, , 14]);
                        config = this.config;
                        useQwen = config.useQwen !== false;
                        endpoint = void 0;
                        headers = void 0;
                        if (!useQwen) return [3 /*break*/, 9];
                        qwenEndpoint = config.endpoint ||
                            "https://dashscope.aliyuncs.com/api/v1/services/aigc/text-generation/generation";
                        headers = __assign(__assign({ "Content-Type": "application/json" }, (config.apiKey && {
                            Authorization: "Bearer ".concat(config.apiKey),
                        })), config.headers);
                        requestBody = config.buildRequestBody
                            ? config.buildRequestBody(params)
                            : this.buildQwenRequestBody(params);
                        // 发送请求
                        console.log("📤 [AiService] 发送请求到 Qwen API:", {
                            endpoint: qwenEndpoint,
                            model: requestBody.model,
                            hasApiKey: !!config.apiKey,
                        });
                        response = void 0;
                        _h.label = 2;
                    case 2:
                        _h.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, fetch(qwenEndpoint, {
                                method: config.method || "POST",
                                headers: headers,
                                body: JSON.stringify(requestBody),
                            })];
                    case 3:
                        response = _h.sent();
                        return [3 /*break*/, 5];
                    case 4:
                        fetchError_1 = _h.sent();
                        // 捕获网络错误（包括 CORS、连接失败等）
                        console.error("❌ [AiService] Fetch 请求失败:", fetchError_1);
                        // 提供更友好的错误信息
                        if (fetchError_1.name === "TypeError" && fetchError_1.message.includes("fetch")) {
                            throw new Error("网络请求失败。可能原因：\n" +
                                "1. CORS 跨域限制（浏览器无法直接调用阿里云 API）\n" +
                                "2. 网络连接问题\n" +
                                "3. API 端点不可达\n" +
                                "建议：通过后端代理调用 Qwen API，或在开发环境中配置代理。\n" +
                                "\u539F\u59CB\u9519\u8BEF: ".concat(fetchError_1.message));
                        }
                        throw fetchError_1;
                    case 5:
                        console.log("📥 [AiService] 收到响应:", {
                            status: response.status,
                            statusText: response.statusText,
                            ok: response.ok,
                        });
                        if (!!response.ok) return [3 /*break*/, 7];
                        return [4 /*yield*/, response.json().catch(function () { return ({}); })];
                    case 6:
                        errorData = _h.sent();
                        errorMessage = errorData.message ||
                            ((_a = errorData.error) === null || _a === void 0 ? void 0 : _a.message) ||
                            errorData.code ||
                            "HTTP error! status: ".concat(response.status);
                        console.error("❌ [AiService] Qwen API 错误:", {
                            status: response.status,
                            error: errorData,
                            message: errorMessage,
                        });
                        throw new Error(errorMessage);
                    case 7: return [4 /*yield*/, response.json()];
                    case 8:
                        result = _h.sent();
                        console.log("✅ [AiService] 响应解析成功");
                        // 解析响应
                        if (config.parseResponse) {
                            return [2 /*return*/, config.parseResponse(result)];
                        }
                        return [2 /*return*/, this.parseQwenResponse(result)];
                    case 9:
                        // 使用自定义 API
                        if (!config.endpoint) {
                            throw new Error("未配置 API 端点");
                        }
                        endpoint = config.endpoint;
                        headers = __assign({ "Content-Type": "application/json" }, config.headers);
                        requestBody = config.buildRequestBody
                            ? config.buildRequestBody(params)
                            : (function () {
                                var data = params.data, thinkingMode = params.thinkingMode, selectedFields = params.selectedFields, rest = __rest(params, ["data", "thinkingMode", "selectedFields"]);
                                return __assign({ data: data, thinkingMode: thinkingMode.id, prompt: thinkingMode.prompt ||
                                        "\u8BF7\u4EE5".concat(thinkingMode.label, "\u7684\u65B9\u5F0F\u5904\u7406\u4EE5\u4E0B\u6570\u636E\uFF1A"), selectedFields: selectedFields }, rest);
                            })();
                        return [4 /*yield*/, fetch(endpoint, {
                                method: config.method || "POST",
                                headers: headers,
                                body: JSON.stringify(requestBody),
                            })];
                    case 10:
                        response = _h.sent();
                        if (!response.ok) {
                            throw new Error("HTTP error! status: ".concat(response.status));
                        }
                        return [4 /*yield*/, response.json()];
                    case 11:
                        result = _h.sent();
                        // 解析响应
                        if (config.parseResponse) {
                            return [2 /*return*/, config.parseResponse(result)];
                        }
                        // 默认解析逻辑
                        return [2 /*return*/, (result.content ||
                                result.text ||
                                result.message ||
                                ((_b = result.data) === null || _b === void 0 ? void 0 : _b.message) ||
                                JSON.stringify(result))];
                    case 12: return [3 /*break*/, 14];
                    case 13:
                        error_1 = _h.sent();
                        console.error("❌ [AiService] AI 调用异常:", {
                            error: error_1,
                            message: error_1.message,
                            stack: error_1.stack,
                            config: {
                                endpoint: (_c = this.config) === null || _c === void 0 ? void 0 : _c.endpoint,
                                useQwen: (_d = this.config) === null || _d === void 0 ? void 0 : _d.useQwen,
                                hasApiKey: !!((_e = this.config) === null || _e === void 0 ? void 0 : _e.apiKey),
                            },
                        });
                        errorMessage = error_1.message || "AI 调用失败";
                        // 如果是网络错误，提供更详细的提示
                        if (errorMessage.includes("Failed to fetch") || errorMessage.includes("网络请求失败")) {
                            errorMessage =
                                "网络请求失败，无法连接到 Qwen API。\n\n" +
                                    "可能的原因：\n" +
                                    "1. CORS 跨域限制：浏览器无法直接调用阿里云 API\n" +
                                    "2. 网络连接问题：请检查网络连接\n" +
                                    "3. API 端点不可达：请检查 endpoint 配置\n\n" +
                                    "解决方案：\n" +
                                    "1. 通过后端代理：在后端服务器上设置代理端点转发请求\n" +
                                    "2. 开发环境：在 webpack/vite 配置中设置代理\n" +
                                    "3. 生产环境：在 nginx 配置中添加代理规则\n\n" +
                                    "\u5F53\u524D endpoint: ".concat(((_f = this.config) === null || _f === void 0 ? void 0 : _f.endpoint) || "https://dashscope.aliyuncs.com/api/v1/services/aigc/text-generation/generation");
                        }
                        finalMessage = ((_g = this.config) === null || _g === void 0 ? void 0 : _g.handleError)
                            ? this.config.handleError(error_1)
                            : errorMessage;
                        throw new Error(finalMessage);
                    case 14: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * 调用 AI 接口（返回完整结果对象）
     */
    AiService.callAiWithResult = function (params) {
        return __awaiter(this, void 0, Promise, function () {
            var message, error_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.callAi(params)];
                    case 1:
                        message = _a.sent();
                        return [2 /*return*/, {
                                success: true,
                                message: message,
                            }];
                    case 2:
                        error_2 = _a.sent();
                        return [2 /*return*/, {
                                success: false,
                                message: "",
                                error: error_2.message || "AI 调用失败",
                            }];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    AiService.config = null;
    /**
     * 默认 API Key（用于开发测试）
     * ⚠️ 警告：这是默认配置，仅用于开发环境
     * 生产环境请使用环境变量或后端 API 调用
     *
     * 默认使用 qwenConfig.example.ts 中配置的密钥
     */
    AiService.DEFAULT_API_KEY = AiService.getEnv("REACT_APP_AI_API_KEY") ||
        AiService.getEnv("NEXT_PUBLIC_AI_API_KEY") ||
        AiService.getEnv("VITE_AI_API_KEY") ||
        "sk-79bb1c54a76d4fbba359a4f49060873a"; // 默认 API Key（来自 qwenConfig.example.ts）
    return AiService;
}());
export { AiService };
/**
 * 默认的 AI 调用函数（兼容旧接口）
 * @deprecated 建议使用 AiService.callAi() 或 AiService.callAiWithResult()
 */
export function callAi(data, thinkingMode, selectedFields) {
    return __awaiter(this, void 0, Promise, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, AiService.callAi({
                    data: data,
                    thinkingMode: thinkingMode,
                    selectedFields: selectedFields,
                })];
        });
    });
}
